import { useState, useEffect } from 'react';
import { Settings, Target, Clock, Users, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';

interface ServiceLevelControlsProps {
  initialSLAPercent?: number;
  initialAnswerTime?: number;
  initialAHT?: number;
  initialShrinkage?: number;
  initialOccupancy?: number;
  onChange: (params: ServiceLevelParams) => void;
}

export interface ServiceLevelParams {
  slaPercent: number;      // 80 = 80%
  answerTime: number;       // seconds
  aht: number;              // minutes
  shrinkage: number;        // 20 = 20%
  occupancy: number;        // 85 = 85%
}

// Preset templates
const PRESETS = {
  STANDARD: { slaPercent: 80, answerTime: 20, aht: 6, shrinkage: 20, occupancy: 85 },
  PREMIUM: { slaPercent: 90, answerTime: 15, aht: 6, shrinkage: 20, occupancy: 80 },
  HIGH_VOLUME: { slaPercent: 70, answerTime: 30, aht: 4, shrinkage: 15, occupancy: 90 },
};

export default function ServiceLevelControls({
  initialSLAPercent = 80,
  initialAnswerTime = 20,
  initialAHT = 6,
  initialShrinkage = 20,
  initialOccupancy = 85,
  onChange,
}: ServiceLevelControlsProps) {
  const [slaPercent, setSLAPercent] = useState(initialSLAPercent);
  const [answerTime, setAnswerTime] = useState(initialAnswerTime);
  const [aht, setAHT] = useState(initialAHT);
  const [shrinkage, setShrinkage] = useState(initialShrinkage);
  const [occupancy, setOccupancy] = useState(initialOccupancy);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Debounced onChange callback
  useEffect(() => {
    setIsCalculating(true);
    const timeout = setTimeout(() => {
      onChange({ slaPercent, answerTime, aht, shrinkage, occupancy });
      setIsCalculating(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [slaPercent, answerTime, aht, shrinkage, occupancy, onChange]);

  const applyPreset = (preset: keyof typeof PRESETS) => {
    const params = PRESETS[preset];
    setSLAPercent(params.slaPercent);
    setAnswerTime(params.answerTime);
    setAHT(params.aht);
    setShrinkage(params.shrinkage);
    setOccupancy(params.occupancy);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Settings className="h-5 w-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">Service Level Controls</h2>
          <span className="text-sm text-gray-500">
            (SLA: {slaPercent}% in {answerTime}s, AHT: {aht}m, Shrinkage: {shrinkage}%, Occupancy: {occupancy}%)
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          {/* Preset Templates */}
          <div className="flex items-center justify-end space-x-2 pt-4 mb-6">
            <span className="text-xs font-medium text-gray-600 mr-2">Quick Presets:</span>
            {(Object.keys(PRESETS) as Array<keyof typeof PRESETS>).map((preset) => (
              <button
                key={preset}
                onClick={() => applyPreset(preset)}
                className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
              >
                {preset.replace('_', ' ')}
              </button>
            ))}
          </div>

      {/* Controls Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* SLA Target */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Target className="h-4 w-4 text-green-600" />
              <label className="text-sm font-medium text-gray-900">Service Level Agreement (SLA)</label>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-green-700">{slaPercent}%</span>
              <span className="text-xs text-gray-500">in</span>
              <input
                type="number"
                value={answerTime}
                onChange={(e) => setAnswerTime(parseInt(e.target.value) || 20)}
                className="w-16 px-2 py-1 text-sm font-semibold text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                min="5"
                max="120"
              />
              <span className="text-xs text-gray-500">sec</span>
            </div>
          </div>
          <input
            type="range"
            min="50"
            max="100"
            step="5"
            value={slaPercent}
            onChange={(e) => setSLAPercent(parseInt(e.target.value))}
            className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>50%</span>
            <span className="text-green-700 font-medium">Target: {slaPercent}% in {answerTime}s</span>
            <span>100%</span>
          </div>
        </div>

        {/* Average Handle Time */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <label className="text-sm font-medium text-gray-900">Average Handle Time (AHT)</label>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-blue-700">{aht}</span>
              <span className="text-xs text-gray-500">min</span>
            </div>
          </div>
          <input
            type="range"
            min="1"
            max="30"
            step="0.5"
            value={aht}
            onChange={(e) => setAHT(parseFloat(e.target.value))}
            className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>1 min</span>
            <span className="text-blue-700 font-medium">{aht} minutes per contact</span>
            <span>30 min</span>
          </div>
        </div>

        {/* Shrinkage Factor */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingDown className="h-4 w-4 text-orange-600" />
              <label className="text-sm font-medium text-gray-900">Shrinkage Factor</label>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-orange-700">{shrinkage}%</span>
            </div>
          </div>
          <input
            type="range"
            min="0"
            max="50"
            step="5"
            value={shrinkage}
            onChange={(e) => setShrinkage(parseInt(e.target.value))}
            className="w-full h-2 bg-orange-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>0%</span>
            <span className="text-orange-700 font-medium">Breaks, meetings, training: {shrinkage}%</span>
            <span>50%</span>
          </div>
        </div>

        {/* Occupancy Target */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-purple-600" />
              <label className="text-sm font-medium text-gray-900">Occupancy Target</label>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-purple-700">{occupancy}%</span>
            </div>
          </div>
          <input
            type="range"
            min="60"
            max="100"
            step="5"
            value={occupancy}
            onChange={(e) => setOccupancy(parseInt(e.target.value))}
            className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>60%</span>
            <span className="text-purple-700 font-medium">Target agent utilization: {occupancy}%</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Calculation Status */}
      {isCalculating && (
        <div className="mt-4 flex items-center justify-center text-sm text-indigo-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
          Recalculating staffing requirements...
        </div>
      )}

          {/* Info Panel */}
          <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">i</span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-indigo-900 mb-1">How it works</h4>
                <p className="text-xs text-indigo-700 leading-relaxed">
                  Adjust these parameters to calculate required staffing. <strong>SLA</strong> defines your service target.
                  <strong> AHT</strong> is average call duration. <strong>Shrinkage</strong> accounts for breaks, meetings, and training.
                  <strong> Occupancy</strong> is the target percentage of time agents spend actively handling contacts.
                  Changes update the staffing grid below in real-time using Erlang C calculations.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
